import React from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, Landmark, ArrowRight, Heart, Briefcase, Sparkles } from "lucide-react";
import FeaturedReviews from "@/components/FeaturedReviews";

const HERO_BG = "https://images.unsplash.com/photo-1478100006428-308e7720aa2f?crop=entropy&cs=srgb&fm=jpg&q=85";
const FAMILY = "https://images.unsplash.com/photo-1648221350871-e3ae3c8d0f58?crop=entropy&cs=srgb&fm=jpg&q=85";
const MENTOR = "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?crop=entropy&cs=srgb&fm=jpg&q=85";
const GROWTH = "https://images.unsplash.com/photo-1772413438631-a4bc7ccf0f4a?crop=entropy&cs=srgb&fm=jpg&q=85";

const pillars = [
  {
    icon: Shield,
    overline: "Pillar I",
    title: "Protect Families",
    copy: "Life insurance and financial protection solutions that create lasting peace of mind for the people who matter most.",
    image: FAMILY,
    testid: "pillar-protect",
  },
  {
    icon: TrendingUp,
    overline: "Pillar II",
    title: "Build Leaders",
    copy: "Mentorship, training, and leadership development that turn service-minded professionals into confident industry leaders.",
    image: MENTOR,
    testid: "pillar-build",
  },
  {
    icon: Landmark,
    overline: "Pillar III",
    title: "Leave a Legacy",
    copy: "Strategies for financial freedom, generational wealth, and the lasting imprint your family deserves to inherit.",
    image: GROWTH,
    testid: "pillar-legacy",
  },
];

const reasons = [
  { Icon: Heart, label: "Promise, not a policy", copy: "We treat every family like our own — coverage built around real lives, not quotas." },
  { Icon: Briefcase, label: "Real career path", copy: "Licensed or new, you get the playbook, mentor, and platform to build a meaningful future." },
  { Icon: Sparkles, label: "Service first", copy: "Integrity. Growth. Family. Legacy. Our core values guide every conversation we have." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Shield architecture" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0D0D0D]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(0,139,139,0.18),_transparent_60%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-40 pb-24 md:pb-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-8 flex items-center gap-4">
                <span className="w-12 h-px bg-[#D4AF37]" /> Established with Purpose · Texas
              </div>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tighter text-white">
                Protect <span className="italic text-[#D4AF37]">Families.</span>
                <br />
                Build <span className="italic text-[#D4AF37]">Leaders.</span>
                <br />
                Leave a <span className="italic text-[#D4AF37]">Legacy.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-8 max-w-md">
                Impact Legacy Group helps families secure their future through life insurance and financial protection — while developing the next generation of leaders.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-cta-protect" className="btn-primary">
                  Get Protected <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/careers" data-testid="hero-cta-join" className="btn-secondary">
                  Join Our Team
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* corner brackets */}
        <div className="absolute top-28 left-6 md:left-10 lg:left-16 w-16 h-16 border-l border-t border-[#D4AF37]/40 pointer-events-none" />
        <div className="absolute bottom-10 right-6 md:right-10 lg:right-16 w-16 h-16 border-r border-b border-[#D4AF37]/40 pointer-events-none" />
      </section>

      {/* WHY US */}
      <section className="relative py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="overline mb-6">— Why Impact Legacy Group</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-8">
              Insurance is more than a policy. <span className="italic text-[#D4AF37]">It's a promise.</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              We help families protect what matters most while creating opportunities for individuals to build meaningful careers, develop leadership skills, and leave a lasting impact on future generations.
            </p>
            <Link to="/about" data-testid="why-cta-about" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.22em] uppercase border-b border-[#D4AF37]/50 pb-1 hover:gap-4 transition-all">
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {reasons.map(({ Icon, label, copy }, i) => (
              <div key={label} className="card-luxury p-8 md:p-10 flex gap-6 group" data-testid={`why-card-${i}`}>
                <div className="shrink-0 w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:text-black transition-all">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.28em] uppercase text-white/40 mb-2">0{i + 1}</div>
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">{label}</h3>
                  <p className="text-white/65 leading-relaxed">{copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="relative py-28 md:py-40 border-t border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,139,139,0.08),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="overline mb-4">— Our Three Pillars</div>
            <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight">
              Built on a foundation of <span className="italic text-[#D4AF37]">three promises.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pillars.map(({ icon: Icon, overline, title, copy, image, testid }) => (
              <article key={title} data-testid={testid} className="card-luxury group flex flex-col overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  <img src={image} alt={title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
                  <div className="absolute top-5 left-5 w-10 h-10 bg-black/70 border border-[#D4AF37]/50 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-8 md:p-10 flex-1">
                  <div className="overline mb-3">{overline}</div>
                  <h3 className="font-serif text-3xl text-white mb-4">{title}</h3>
                  <p className="text-white/65 leading-relaxed">{copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STATS / CTA STRIP */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-4 gap-10 items-center">
          {[
            { k: "5+", v: "Insurance Solutions" },
            { k: "1:1", v: "Mentorship Model" },
            { k: "100%", v: "Remote Friendly" },
            { k: "TX", v: "Proudly Texas-Based" },
          ].map((s, i) => (
            <div key={s.v} data-testid={`stat-${i}`} className="border-l border-[#D4AF37]/30 pl-6">
              <div className="font-serif text-5xl md:text-6xl text-white mb-2">{s.k}</div>
              <div className="overline">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED REVIEWS */}
      <FeaturedReviews />
    </div>
  );
}
